import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import {
  Database,
  Users,
  Activity,
  Download,
  Upload,
  RefreshCw,
  FileText,
  AlertCircle,
  CheckCircle,
  Server,
  Cloud,
  CalendarClock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminConsole = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dbStats, setDbStats] = useState(null);
  const [logs, setLogs] = useState('');
  const [logSource, setLogSource] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeWindow, setActiveWindow] = useState(480);
  const [activeFetchedAt, setActiveFetchedAt] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBackupConfirm, setShowBackupConfirm] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [backupData, setBackupData] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // Redirect if not super admin
  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">This page is only accessible to Super Administrators</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchDatabaseStats();
    fetchRecentActivity();
    fetchPendingRegistrations();
    fetchBackups();
  }, []);

  // Active users: refetch when the window changes and every 60s while the console is open
  useEffect(() => {
    fetchActiveUsers(activeWindow);
    const t = setInterval(() => fetchActiveUsers(activeWindow), 60000);
    return () => clearInterval(t);
  }, [activeWindow]);

  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

  const fetchPendingRegistrations = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/pending-registrations`, { headers: authHeaders() });
      if (r.ok) setPendingRegistrations(await r.json());
    } catch {}
  };

  const fetchDatabaseStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/database-stats`, { headers: authHeaders() });
      if (response.ok) {
        setDbStats(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch database stats:', error);
    }
  };

  const fetchBackups = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/backups`, { headers: authHeaders() });
      if (r.ok) setBackupData(await r.json());
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    }
  };

  const fetchLogs = async (logType) => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/logs?type=${logType}&lines=500`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setLogSource(data.source || '');
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveUsers = async (minutes = activeWindow) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/active-users?minutes=${minutes}`, { headers: authHeaders() });
      if (response.ok) {
        setActiveUsers(await response.json());
        setActiveFetchedAt(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch active users:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/recent-activity?limit=50`, { headers: authHeaders() });
      if (response.ok) {
        setRecentActivity(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
    }
  };

  const handleBackup = async () => {
    try {
      setLoading(true);
      setShowBackupConfirm(false);
      const response = await fetch(`${BACKEND_URL}/api/admin/backup-database`, {
        method: 'POST',
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setLastBackup(data);
        fetchDatabaseStats();
        fetchBackups();
      } else {
        const error = await response.json();
        alert(`❌ Backup failed: ${error.detail}`);
      }
    } catch (error) {
      alert(`❌ Backup failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const [restoringId, setRestoringId] = useState(null);
  const restoreSchedule = async (backupId) => {
    if (!backupId) return;
    setRestoringId(backupId);
    try {
      const post = async (dry) => {
        const r = await fetch(`${BACKEND_URL}/api/admin/restore-work-periods`, {
          method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ backup_id: backupId, dry_run: dry }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.detail || 'Restore failed');
        return data;
      };
      const dry = await post(true);
      if (!dry.missing) { alert(`✅ Nothing to restore — all ${dry.in_backup} work periods in this backup already exist on the schedule.`); return; }
      const sample = dry.preview.slice(0, 10).map((w) => `• ${w.project_id}  ${w.start_date} → ${w.end_date}${w.headcount ? `  (${w.crew.join(', ')}${w.headcount > w.crew.length ? ', …' : ''})` : ''}`).join('\n');
      const ok = window.confirm(`Restore the Millwright Schedule from this backup?\n\n${dry.missing} work periods are in the backup but missing today (${dry.already_present} already present and left untouched).\n\n${sample}${dry.missing > 10 ? `\n…and ${dry.missing - 10} more` : ''}\n\nOnly the missing work periods are added back — nothing else in the database changes.`);
      if (!ok) return;
      const res = await post(false);
      alert(`✅ Restored ${res.restored} work periods to the schedule.\n\nNext: open Manpower Sheet → "Read sheet now" to pull anything scheduled since the backup, and use "Reconcile w/ Timesheets" on the Schedule for past days.`);
    } catch (error) {
      alert(`❌ Restore failed: ${error.message}`);
    } finally {
      setRestoringId(null);
    }
  };

  const downloadBackup = async (backupId) => {
    if (!backupId) return;
    setDownloadingId(backupId);
    try {
      const url = `${BACKEND_URL}/api/admin/download-backup/${backupId}`;
      const response = await fetch(url, { headers: authHeaders() });
      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `365Ops_Backup_${backupId}.zip`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(link.href);
          document.body.removeChild(link);
        }, 100);
      } else {
        const error = await response.text();
        alert(`❌ Download failed: ${error}`);
      }
    } catch (error) {
      alert(`❌ Download failed: ${error.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const windowLabel = (m) => (m < 60 ? `${m} minutes` : m < 1440 ? `${Math.round(m / 60)} hour${m >= 120 ? 's' : ''}` : `${Math.round(m / 1440)} day${m >= 2880 ? 's' : ''}`);
  const timeAgo = (iso) => {
    if (!iso) return '—';
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 90) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)} min ago`;
    if (s < 86400) return `${Math.round(s / 3600)} h ago`;
    return `${Math.round(s / 86400)} d ago`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { timeZone: 'America/Toronto' });
  };

  const firstIp = (ip) => {
    if (!ip) return 'N/A';
    return String(ip).split(',')[0].trim() || 'N/A';
  };

  const activityLabel = (a) => {
    if (a.action) return a.action;
    if (a.type === 'login') return 'Logged in';
    if (a.type === 'role_change') return `Changed role: ${a.detail || ''}`;
    return a.type || 'Activity';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Super Admin Console</h1>
          <p className="text-gray-600">System monitoring, database management, and troubleshooting</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6 border border-gray-200">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              data-testid="admin-tab-overview"
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Server className="inline-block mr-2 h-5 w-5" />
              Overview
            </button>
            <button
              data-testid="admin-tab-database"
              onClick={() => setActiveTab('database')}
              className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'database'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Database className="inline-block mr-2 h-5 w-5" />
              Database
            </button>
            <button
              data-testid="admin-tab-logs"
              onClick={() => setActiveTab('logs')}
              className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'logs'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="inline-block mr-2 h-5 w-5" />
              Server Logs
            </button>
            <button
              data-testid="admin-tab-users"
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'users'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="inline-block mr-2 h-5 w-5" />
              Active Users
            </button>
            <button
              data-testid="admin-tab-activity"
              onClick={() => setActiveTab('activity')}
              className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'activity'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Activity className="inline-block mr-2 h-5 w-5" />
              Activity Log
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Pending Registrations */}
            {pendingRegistrations.length > 0 && (
              <div className="bg-amber-50 rounded-lg shadow-sm p-6 border border-amber-200">
                <h3 className="text-lg font-semibold text-amber-900 mb-3">Pending Account Approvals ({pendingRegistrations.length})</h3>
                <div className="space-y-2">
                  {pendingRegistrations.map(u => (
                    <div key={u.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{u.full_name}</p>
                        <p className="text-xs text-slate-600">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select id={`role-${u.id}`} defaultValue="labourer"
                          className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs text-slate-900 outline-none">
                          <option value="super_admin">Super Admin</option>
                          <option value="office_admin">Office Admin</option>
                          <option value="fabrication_manager">Fab Manager</option>
                          <option value="project_manager_estimator">PM/Estimator</option>
                          <option value="dispatch">Dispatch</option>
                          <option value="shop_foreman">Shop Foreman</option>
                          <option value="millwright_foreman">Millwright Foreman</option>
                          <option value="millwright">Millwright</option>
                          <option value="welder">Welder - Fitter</option>
                          <option value="labourer">Labourer</option>
                          <option value="tool_crib">Tool Crib</option>
                        </select>
                        <button onClick={async () => {
                          const role = document.getElementById(`role-${u.id}`).value;
                          await fetch(`${BACKEND_URL}/api/admin/approve-registration/${u.id}`, { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ role }) });
                          fetchPendingRegistrations();
                        }} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-medium">Approve</button>
                        <button onClick={async () => {
                          if (!window.confirm(`Reject and delete ${u.full_name}'s account?`)) return;
                          await fetch(`${BACKEND_URL}/api/admin/reject-registration/${u.id}`, { method: 'POST', headers: authHeaders() });
                          fetchPendingRegistrations();
                        }} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium">Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Database Stats */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Database Health</h3>
                <Database className="h-8 w-8 text-blue-600" />
              </div>
              {dbStats ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Total Size</p>
                    <p className="text-2xl font-bold text-gray-900">{formatBytes(dbStats.total_size)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Collections</p>
                    <p className="text-xl font-semibold text-gray-900">{dbStats.collections_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-green-600 font-medium">Connected</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="animate-pulse space-y-3">
                  <div className="h-8 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                </div>
              )}
            </div>

            {/* Active Users */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Active Users</h3>
                <Users className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="text-4xl font-bold text-gray-900" data-testid="admin-active-user-count">{activeUsers.length}</p>
                <p className="text-sm text-gray-600 mt-2">Used the app in the last {windowLabel(activeWindow)}</p>
                <p className="text-xs text-emerald-700 font-semibold mt-1" data-testid="admin-online-now-count">
                  {activeUsers.filter(u => u.online_now).length} online right now
                </p>
              </div>
            </div>

            {/* Weekly Backup Status */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Weekly Backup</h3>
                <Cloud className="h-8 w-8 text-purple-600" />
              </div>
              <div data-testid="admin-weekly-backup-card">
                {backupData?.weekly ? (
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Last backup</p>
                    <p className="text-lg font-bold text-gray-900">{backupData.weekly.last_backup_at ? formatDate(backupData.weekly.last_backup_at) : 'Never'}</p>
                    <p className="text-xs text-gray-500">Auto-runs weekly → Google Drive "{backupData.weekly.drive_folder}"</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Loading…</p>
                )}
              </div>
            </div>
          </div>
          </div>
        )}

        {activeTab === 'database' && (
          <div className="space-y-6">
            {/* Weekly backup banner */}
            <div className="bg-purple-50 rounded-lg shadow-sm p-4 border border-purple-200 flex items-start gap-3" data-testid="weekly-backup-banner">
              <CalendarClock className="h-6 w-6 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-purple-900">Weekly automated backups are ON</p>
                <p className="text-xs text-purple-700 mt-1">
                  A full backup runs automatically every 7 days and is uploaded to the "{backupData?.weekly?.drive_folder || '365Ops Backups'}" folder in Google Drive
                  (keeps last {backupData?.weekly?.keep_drive || 12} in Drive, last {backupData?.weekly?.keep_local || 4} on the server).
                  {backupData?.weekly?.last_backup_at
                    ? ` Last backup: ${formatDate(backupData.weekly.last_backup_at)} · Next due: ${formatDate(backupData.weekly.next_due)}`
                    : ' First backup will run automatically within a few minutes of deployment.'}
                </p>
              </div>
            </div>

            {/* Database Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Database Management</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  data-testid="create-backup-btn"
                  onClick={() => setShowBackupConfirm(true)}
                  disabled={loading}
                  className="flex items-center justify-center gap-3 px-6 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                >
                  {loading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
                  {loading ? 'Backing up…' : 'Create Backup Now'}
                </button>
                <button
                  data-testid="restore-backup-btn"
                  onClick={() => setShowRestoreModal(true)}
                  className="flex items-center justify-center gap-3 px-6 py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Upload className="h-5 w-5" />
                  Restore from Backup
                </button>
              </div>

              {lastBackup && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="backup-success-banner">
                  <p className="text-sm text-green-800 font-semibold mb-2">
                    ✅ Backup Created Successfully!
                  </p>
                  <p className="text-sm text-green-700">
                    <strong>ID:</strong> {lastBackup.backup_id} |
                    <strong> Size:</strong> {(lastBackup.size_mb).toFixed(2)} MB |
                    <strong> Collections:</strong> {lastBackup.collections_count}
                  </p>
                  {lastBackup.drive_link ? (
                    <p className="text-xs text-green-700 mt-2">
                      ☁️ Saved to Google Drive — <a href={lastBackup.drive_link} target="_blank" rel="noreferrer" className="underline font-semibold">open in Drive</a>
                    </p>
                  ) : lastBackup.drive_error ? (
                    <p className="text-xs text-amber-700 mt-2">⚠️ Drive upload failed: {lastBackup.drive_error}</p>
                  ) : null}
                </div>
              )}
            </div>

            {/* Previous Backups */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200" data-testid="previous-backups-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Previous Backups</h3>
                <button onClick={fetchBackups} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="refresh-backups-btn">
                  <RefreshCw className="h-4 w-4" /> Refresh
                </button>
              </div>
              {!backupData || backupData.backups.length === 0 ? (
                <p className="text-gray-600 text-sm" data-testid="no-backups-msg">No backups yet — create one above, or wait for the weekly auto-backup.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documents</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {backupData.backups.map((b) => (
                        <tr key={b.backup_id} className="hover:bg-gray-50" data-testid={`backup-row-${b.backup_id}`}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatDate(b.timestamp)}</td>
                          <td className="px-4 py-3 text-sm">
                            {!b.success ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700" title={b.error}>Failed</span>
                            ) : b.trigger === 'weekly' ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Weekly auto</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Manual</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{b.size_mb ? `${b.size_mb.toFixed(1)} MB` : '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{b.total_documents ? b.total_documents.toLocaleString() : '—'}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              {b.local_available && (
                                <button
                                  data-testid={`download-backup-${b.backup_id}`}
                                  onClick={() => downloadBackup(b.backup_id)}
                                  disabled={downloadingId === b.backup_id}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-xs font-medium"
                                >
                                  {downloadingId === b.backup_id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                  Download
                                </button>
                              )}
                              {b.drive_link && (
                                <a
                                  data-testid={`drive-backup-${b.backup_id}`}
                                  href={b.drive_link} target="_blank" rel="noreferrer"
                                  className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium"
                                >
                                  <Cloud className="h-3.5 w-3.5" /> Drive
                                </a>
                              )}
                              {b.restorable && (
                                <button
                                  data-testid={`restore-schedule-${b.backup_id}`}
                                  onClick={() => restoreSchedule(b.backup_id)}
                                  disabled={restoringId === b.backup_id}
                                  title="Put back any work periods that exist in this backup but are missing from the Millwright Schedule today. Nothing else is touched."
                                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white rounded-lg text-xs font-medium"
                                >
                                  {restoringId === b.backup_id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                  Restore schedule
                                </button>
                              )}
                              {!b.local_available && !b.drive_link && b.success && (
                                <span className="text-xs text-slate-500">File no longer on server</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Collection Stats */}
            {dbStats && dbStats.collections && (
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Collection Statistics</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collection</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documents</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Doc Size</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {dbStats.collections.map((col) => (
                        <tr key={col.name} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{col.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{col.count.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(col.size)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(col.avg_size)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex flex-wrap gap-3 mb-4">
                <button
                  data-testid="logs-backend-btn"
                  onClick={() => fetchLogs('backend')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                >
                  Backend Logs
                </button>
                <button
                  data-testid="logs-app-btn"
                  onClick={() => fetchLogs('app')}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                >
                  App Logs (live)
                </button>
                <button
                  data-testid="logs-error-btn"
                  onClick={() => fetchLogs('error')}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                >
                  Error Logs
                </button>
                {logSource && (
                  <span className="self-center text-xs text-gray-500">
                    Source: {logSource === 'file' ? 'server log file' : 'in-app log buffer'}
                  </span>
                )}
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
                  <p className="text-gray-600 mt-2">Loading logs...</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg p-4 overflow-x-auto">
                  <pre className="text-green-700 text-xs font-mono whitespace-pre-wrap" data-testid="logs-output">
                    {logs || 'Select a log type to view'}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Active Users <span className="text-sm font-normal text-gray-500">— anyone who used the app in the last {windowLabel(activeWindow)}</span></h3>
              <div className="flex items-center gap-2">
                <select value={activeWindow} onChange={e => setActiveWindow(Number(e.target.value))} data-testid="active-users-window"
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-800">
                  <option value={15}>Last 15 minutes</option>
                  <option value={60}>Last hour</option>
                  <option value={480}>Last 8 hours</option>
                  <option value={1440}>Last 24 hours</option>
                  <option value={10080}>Last 7 days</option>
                  <option value={43200}>Last 30 days</option>
                </select>
                <button onClick={() => fetchActiveUsers(activeWindow)} data-testid="active-users-refresh" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Refresh</button>
                {activeFetchedAt && <span className="text-xs text-slate-500">updated {activeFetchedAt.toLocaleTimeString()} · auto-refreshes every minute</span>}
              </div>
            </div>
            {activeUsers.length === 0 ? (
              <p className="text-gray-600" data-testid="no-active-users-msg">Nobody has used the app in the last {windowLabel(activeWindow)}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" data-testid="active-users-table">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Page</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activeUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50" data-testid={`active-user-row-${u.id}`}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <span className="inline-flex items-center gap-2">
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${u.online_now ? 'bg-emerald-500' : 'bg-gray-300'}`} title={u.online_now ? 'Online now' : 'Idle'} />
                            {u.full_name}
                          </span>
                          <span className="block text-xs text-gray-500 font-normal pl-[18px]">{u.email}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{u.role}</td>
                        <td className="px-4 py-3 text-sm text-gray-800" data-testid={`active-user-last-active-${u.id}`}>
                          <span className={u.online_now ? 'text-emerald-700 font-semibold' : ''}>{timeAgo(u.last_active)}</span>
                          <span className="block text-xs text-slate-500">{formatDate(u.last_active)}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 font-mono">{(u.last_seen_path || '').replace(/^\/api\//, '')}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(u.last_login)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{firstIp(u.last_login_ip)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity Log</h3>
            {recentActivity.length === 0 ? (
              <p className="text-gray-600" data-testid="no-activity-msg">No activity recorded yet</p>
            ) : (
              <div className="space-y-3" data-testid="activity-log-list">
                {recentActivity.map((activity, idx) => (
                  <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50 rounded-r" data-testid={`activity-row-${idx}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{activity.user_name || activity.user_email || 'Unknown user'}</p>
                        <p className="text-sm text-gray-600">
                          {activityLabel(activity)}
                          {activity.user_email && <span className="text-slate-500"> · {activity.user_email}</span>}
                          {activity.ip && <span className="text-slate-500"> · {firstIp(activity.ip)}</span>}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-3">{formatDate(activity.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Backup Confirmation Modal */}
      {showBackupConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Create Database Backup?</h3>
            <p className="text-gray-600 mb-6">
              This creates a complete backup of every collection and uploads it to the
              "365Ops Backups" folder in Google Drive. This may take a minute.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBackupConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-backup-btn"
                onClick={handleBackup}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
              >
                Create Backup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-900 mb-2">⚠️ Restore Database</h3>
            <p className="text-gray-600 mb-4">
              Upload a backup ZIP file to restore the database.
            </p>
            <p className="text-red-600 text-sm font-semibold mb-6">
              WARNING: This will REPLACE all current data with the backup data!
            </p>
            <input
              type="file"
              accept=".zip"
              data-testid="restore-file-input"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const formData = new FormData();
                formData.append('backup_file', file);

                try {
                  setLoading(true);
                  const response = await fetch(`${BACKEND_URL}/api/admin/restore-database`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: formData
                  });

                  if (response.ok) {
                    const data = await response.json();
                    alert(`✅ Restore successful!\n\nCollections: ${data.collections_restored}\nDocuments: ${data.total_documents?.toLocaleString()}`);
                    setShowRestoreModal(false);
                    fetchDatabaseStats();
                  } else {
                    const error = await response.json();
                    alert(`❌ Restore failed: ${error.detail}`);
                  }
                } catch (error) {
                  alert(`❌ Restore failed: ${error.message}`);
                } finally {
                  setLoading(false);
                }
              }}
              className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminConsole;
